import React, { useState, useEffect } from 'react';
import { Modal } from '../../atoms/Modal';
import { Button } from '../../atoms/Button';
import { FormField } from '../../molecules/FormField';
import { Icon } from '@lnr-v2/ui';
import type {
  Tag,
  TagCreateInput,
  TagUpdateInput,
} from '../../../services/tagsService';
import styles from './TagFormModal.module.css';

export interface TagFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TagCreateInput | TagUpdateInput) => Promise<void>;
  tag?: Tag;
  mode: 'create' | 'edit';
}

interface FormData {
  nameEN: string;
  nameFR: string;
  description: string;
  color: string;
  icon: string;
  category: string;
  sortOrder: string;
  isActive: boolean;
}

interface FormErrors {
  nameEN?: string;
  nameFR?: string;
  color?: string;
  sortOrder?: string;
}

const DEFAULT_FORM_DATA: FormData = {
  nameEN: '',
  nameFR: '',
  description: '',
  color: '',
  icon: '',
  category: '',
  sortOrder: '0',
  isActive: true,
};

export const TagFormModal: React.FC<TagFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  tag,
  mode,
}) => {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && tag) {
        setFormData({
          nameEN: tag.nameEN,
          nameFR: tag.nameFR,
          description: tag.description || '',
          color: tag.color || '',
          icon: tag.icon || '',
          category: tag.category || '',
          sortOrder: String(tag.sortOrder),
          isActive: tag.isActive,
        });
      } else {
        setFormData(DEFAULT_FORM_DATA);
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, mode, tag]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      isActive: e.target.checked,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.nameEN.trim()) {
      newErrors.nameEN = 'English name is required';
    }

    if (!formData.nameFR.trim()) {
      newErrors.nameFR = 'French name is required';
    }

    if (formData.color && !/^#[0-9A-F]{6}$/i.test(formData.color)) {
      newErrors.color = 'Color must be a valid hex code (e.g., #FF5733)';
    }

    const sortOrderNum = parseInt(formData.sortOrder, 10);
    if (isNaN(sortOrderNum) || sortOrderNum < 0) {
      newErrors.sortOrder = 'Sort order must be a non-negative number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData: TagCreateInput | TagUpdateInput = {
        nameEN: formData.nameEN.trim(),
        nameFR: formData.nameFR.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color.trim() || undefined,
        icon: formData.icon.trim() || undefined,
        category: formData.category.trim() || undefined,
        sortOrder: parseInt(formData.sortOrder, 10),
      };

      if (mode === 'edit') {
        (submitData as TagUpdateInput).isActive = formData.isActive;
      }

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Failed to submit tag:', error);
      setIsSubmitting(false);
    }
  };

  const modalTitle = mode === 'create' ? 'Create New Tag' : 'Edit Tag';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="medium"
      closeOnBackdropClick={!isSubmitting}
      closeOnEscapeKey={!isSubmitting}
      showCloseButton={!isSubmitting}
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formContent}>
          <div className={styles.row}>
            <FormField
              label="Name (English)"
              name="nameEN"
              type="text"
              value={formData.nameEN}
              onChange={handleChange}
              error={errors.nameEN}
              required
              placeholder="e.g., Web Development"
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.row}>
            <FormField
              label="Name (French)"
              name="nameFR"
              type="text"
              value={formData.nameFR}
              onChange={handleChange}
              error={errors.nameFR}
              required
              placeholder="e.g., Développement Web"
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.row}>
            <FormField
              label="Description"
              name="description"
              type="textarea"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description for this tag"
              rows={3}
              disabled={isSubmitting}
              helpText="Optional: Provide additional context about this tag"
            />
          </div>

          <div className={styles.row}>
            <div className={styles.colorField}>
              <FormField
                label="Color"
                name="color"
                type="text"
                value={formData.color}
                onChange={handleChange}
                error={errors.color}
                placeholder="#FF5733"
                disabled={isSubmitting}
                helpText="Hex color code (e.g., #FF5733)"
              />
              {formData.color && /^#[0-9A-F]{6}$/i.test(formData.color) && (
                <div className={styles.colorPreview}>
                  <div
                    className={styles.colorSwatch}
                    style={{ backgroundColor: formData.color }}
                  />
                  <span className={styles.colorLabel}>Preview</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.row}>
            <FormField
              label="Icon"
              name="icon"
              type="text"
              value={formData.icon}
              onChange={handleChange}
              placeholder="e.g., code, design, mobile"
              disabled={isSubmitting}
              helpText="Optional: Icon identifier from your icon library"
            />
          </div>

          <div className={styles.row}>
            <FormField
              label="Category"
              name="category"
              type="text"
              value={formData.category}
              onChange={handleChange}
              placeholder="e.g., Technology, Design, Marketing"
              disabled={isSubmitting}
              helpText="Optional: Group tags by category"
            />
          </div>

          <div className={styles.row}>
            <FormField
              label="Sort Order"
              name="sortOrder"
              type="number"
              value={formData.sortOrder}
              onChange={handleChange}
              error={errors.sortOrder}
              min={0}
              disabled={isSubmitting}
              helpText="Lower numbers appear first (default: 0)"
            />
          </div>

          {mode === 'edit' && (
            <div className={styles.row}>
              <div className={styles.checkboxField}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleCheckboxChange}
                    disabled={isSubmitting}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Active</span>
                </label>
                <p className={styles.checkboxHelp}>
                  Inactive tags are hidden from public view
                </p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            leftIcon={
              !isSubmitting && (
                <Icon
                  name={mode === 'create' ? 'plus' : 'save'}
                  size="sm"
                  color="currentColor"
                />
              )
            }
          >
            {mode === 'create' ? 'Create Tag' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
